import * as React from "react";
import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { AuthManager } from "../../component/auth/AuthContext";
import DemoStudyApi, { DemoStudy } from "../../api/DemoStudyApi";
import DemoStudyDetail from "../demo-study-detail/DemoStudyDetail";
import i18n from "i18next";

interface DemoStudyListProps extends RouteComponentProps<string> {
  authManager: AuthManager;
}

interface DemoStudyListState {
  loading: boolean;
  items: Array<DemoStudy>;
  code: string;
  currentDemoStudy: DemoStudy;
  selectedIndex?: number;
}

export default class DemoStudyList extends Component<DemoStudyListProps, DemoStudyListState> {
  private demoStudyApi: DemoStudyApi;

  constructor(props: DemoStudyListProps) {
    super(props);

    this.demoStudyApi = DemoStudyApi.with(props.authManager.cancelableClient());

    this.state = {
      loading: false,
      items: [],
      code: "",
      currentDemoStudy: undefined,
    };
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.onWindowClose);
    this.load();
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.onWindowClose);
    this.demoStudyApi.cancel();
  }

  onWindowClose(ev: BeforeUnloadEvent) {
    ev.preventDefault();
    return (ev.returnValue = i18n.t("demoStudyList.closeValidation"));
  }

  load() {
    this.setState({ loading: true, selectedIndex: undefined }, () => {
      this.demoStudyApi.findStudies(this.state.code).then((demoEntries) => {
        this.setState({
          loading: false,
          items: demoEntries,
        });
      });
    });
  }

  onClickStudy(demo: DemoStudy) {
    this.setState({ currentDemoStudy: demo, selectedIndex: this.getIndex(demo) });
  }

  onCloseDetail() {
    this.setState({ currentDemoStudy: undefined });
  }

  onNext() {
    const newIndex = this.getCurrentDemoIndex() + 1;
    this.setState({ currentDemoStudy: this.state.items[newIndex], selectedIndex: newIndex });
  }

  hasNextItem() {
    return this.getCurrentDemoIndex() < this.state.items.length - 1;
  }

  onPrevious() {
    const newIndex = this.getCurrentDemoIndex() - 1;
    this.setState({ currentDemoStudy: this.state.items[newIndex], selectedIndex: newIndex });
  }

  hasPreviousItem() {
    return this.getCurrentDemoIndex() !== 0;
  }

  private getCurrentDemoIndex() {
    return this.getIndex(this.state.currentDemoStudy);
  }

  private getIndex(demo: DemoStudy) {
    return this.state.items.indexOf(demo);
  }

  render() {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center pb-5">
          <form onSubmit={($) => this.load()} className="input-group col-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={this.state.code}
              onChange={(evt) => this.setState({ code: evt.target.value })}
            />
            <div className="input-group-append">
              <button type="submit" className="btn btn-primary">
                <i className="fa fa-search" />
              </button>
            </div>
          </form>
        </div>

        <table className="table table-striped table-dark">
          <thead>
            <tr>
              <th>{i18n.t("demoStudyList.number")}</th>
              <th>{i18n.t("demoStudyList.label")}</th>
              <th>{i18n.t("demoStudyList.bodyPart")}</th>
            </tr>
          </thead>
          <tbody>
            {this.state.items.map((demo, index) => (
              <tr
                key={demo.study.id}
                className={this.state.selectedIndex === index ? "bg-info" : ""}
                onClick={($) => this.onClickStudy(demo)}
              >
                <td>{demo.number}</td>
                <td>{demo.label}</td>
                <td>{demo.study.series.map((series) => series.bodyPartExamined).reduce((prev) => prev)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {this.state.loading && (
          <div className="text-center">
            <div>
              <i className="fa fa-spinner fa-spin" />
            </div>
          </div>
        )}
        {this.state.currentDemoStudy && (
          <DemoStudyDetail
            {...this.props}
            study={this.state.currentDemoStudy.study}
            actions={
              <div className="btn-group">
                <button
                  className="btn btn-primary px-2"
                  title={i18n.t("global.next")}
                  disabled={!this.hasPreviousItem()}
                  onClick={($) => this.onPrevious()}
                >
                  <i className="fa fa-arrow-left" />
                </button>

                <button
                  className="btn btn-primary px-2"
                  title={i18n.t("global.next")}
                  disabled={!this.hasNextItem()}
                  onClick={($) => this.onNext()}
                >
                  <i className="fa fa-arrow-right" />
                </button>

                <button
                  className="btn btn-secondary px-2"
                  title={i18n.t("global.close")}
                  onClick={($) => this.onCloseDetail()}
                >
                  <i className="fa fa-close" />
                </button>
              </div>
            }
          />
        )}
      </div>
    );
  }
}
